import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Search, FileText, AlertCircle } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface Contract {
  id: number;
  vendor_name: string;
  vendor_id: number;
  type: string;
  title: string;
  dpa_status: string;
  signed_date: string;
  expiry_date: string;
  status: string;
  notes: string;
  created_at: string;
}

interface Certification {
  id: number;
  vendor_name: string;
  vendor_id: number;
  cert_name: string;
  issuer: string;
  issue_date: string;
  expiry_date: string;
  status: string;
  certificate_url: string;
}

interface Subprocessor {
  id: number;
  vendor_name: string;
  vendor_id: number;
  subprocessor_name: string;
  service: string;
  location: string;
  dpa_in_place: boolean;
  notes: string;
}

interface DpaStatus {
  total_vendors: number;
  dpa_signed: number;
  dpa_pending: number;
  dpa_not_required: number;
  dpa_missing: number;
  expiring_soon: number;
}

type Tab = 'contracts' | 'certifications' | 'subprocessors';

const dpaStatusColors: Record<string, string> = {
  signed: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  not_required: 'bg-gray-100 text-gray-600',
  missing: 'bg-red-100 text-red-800',
  expired: 'bg-red-100 text-red-800',
};

const contractStatusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  expiring: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  draft: 'bg-gray-100 text-gray-800',
  terminated: 'bg-red-100 text-red-800',
};

const certStatusColors: Record<string, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-800',
};

const EMPTY_CONTRACT = { vendor_id: '', type: 'service_agreement', title: '', dpa_status: 'pending', signed_date: '', expiry_date: '', notes: '' };
const EMPTY_CERT = { vendor_id: '', cert_name: '', issuer: '', issue_date: '', expiry_date: '' };
const EMPTY_SUB = { vendor_id: '', subprocessor_name: '', service: '', location: '', dpa_in_place: false, notes: '' };

const VendorContracts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('contracts');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [subprocessors, setSubprocessors] = useState<Subprocessor[]>([]);
  const [dpaStatus, setDpaStatus] = useState<DpaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [contractModal, setContractModal] = useState(false);
  const [contractForm, setContractForm] = useState(EMPTY_CONTRACT);
  const [certModal, setCertModal] = useState(false);
  const [certForm, setCertForm] = useState(EMPTY_CERT);
  const [subModal, setSubModal] = useState(false);
  const [subForm, setSubForm] = useState(EMPTY_SUB);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);

      const [dpaRes, contractsRes, certsRes, subsRes] = await Promise.all([
        fetch('/api/compliance/vendor-contracts/dpa-status', { headers: authHeaders() }).then(r => r.json()),
        fetch(`/api/compliance/vendor-contracts?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`/api/compliance/vendor-contracts/certifications?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`/api/compliance/vendor-contracts/subprocessors?${params}`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setDpaStatus(dpaRes);
      setContracts(contractsRes.contracts || []);
      setCertifications(certsRes.certifications || []);
      setSubprocessors(subsRes.subprocessors || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleCreateContract = async () => {
    if (!contractForm.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/vendor-contracts', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...contractForm, vendor_id: contractForm.vendor_id ? Number(contractForm.vendor_id) : undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      setContractModal(false);
      setContractForm(EMPTY_CONTRACT);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCert = async () => {
    if (!certForm.cert_name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/vendor-contracts/certifications', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...certForm, vendor_id: certForm.vendor_id ? Number(certForm.vendor_id) : undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      setCertModal(false);
      setCertForm(EMPTY_CERT);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSub = async () => {
    if (!subForm.subprocessor_name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/vendor-contracts/subprocessors', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...subForm, vendor_id: subForm.vendor_id ? Number(subForm.vendor_id) : undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      setSubModal(false);
      setSubForm(EMPTY_SUB);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isExpiringSoon = (date: string) => {
    if (!date) return false;
    const diff = new Date(date).getTime() - Date.now();
    return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000;
  };

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'contracts', label: 'Contracts', count: contracts.length },
    { key: 'certifications', label: 'Certifications', count: certifications.length },
    { key: 'subprocessors', label: 'Subprocessors', count: subprocessors.length },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Vendor Contracts & DPA</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          {activeTab === 'contracts' && (
            <button onClick={() => { setContractForm(EMPTY_CONTRACT); setContractModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
              <Plus size={16} /> Add Contract
            </button>
          )}
          {activeTab === 'certifications' && (
            <button onClick={() => { setCertForm(EMPTY_CERT); setCertModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
              <Plus size={16} /> Add Certification
            </button>
          )}
          {activeTab === 'subprocessors' && (
            <button onClick={() => { setSubForm(EMPTY_SUB); setSubModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
              <Plus size={16} /> Add Subprocessor
            </button>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {/* DPA Status Summary */}
      {dpaStatus && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Vendors', value: dpaStatus.total_vendors, color: 'text-gray-900' },
            { label: 'DPA Signed', value: dpaStatus.dpa_signed, color: 'text-green-600' },
            { label: 'DPA Pending', value: dpaStatus.dpa_pending, color: 'text-yellow-600' },
            { label: 'DPA Missing', value: dpaStatus.dpa_missing, color: 'text-red-600' },
            { label: 'Expiring Soon', value: dpaStatus.expiring_soon, color: 'text-orange-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label} <span className="ml-1 text-xs text-gray-400">({t.count})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : activeTab === 'contracts' ? (
          contracts.length === 0 ? (
            <div className="py-12 text-gray-500">No contracts found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Vendor', 'Type', 'Title', 'DPA Status', 'Signed', 'Expiry', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {contracts.map(c => (
                  <tr key={c.id} className={`hover:bg-gray-50 ${isExpiringSoon(c.expiry_date) ? 'bg-yellow-50/50' : ''}`}>
                    <td className="px-4 py-3 text-left font-medium text-gray-900">{c.vendor_name}</td>
                    <td className="px-4 py-3 text-left capitalize text-gray-600">{c.type?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-700">{c.title}</td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${dpaStatusColors[c.dpa_status] || 'bg-gray-100 text-gray-700'}`}>
                        {c.dpa_status?.replace(/_/g, ' ') || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-gray-600">{c.signed_date ? new Date(c.signed_date).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">
                      {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '—'}
                      {isExpiringSoon(c.expiry_date) && (
                        <span className="ml-1 inline-flex items-center"><AlertCircle size={12} className="text-yellow-600" /></span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${contractStatusColors[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : activeTab === 'certifications' ? (
          certifications.length === 0 ? (
            <div className="py-12 text-gray-500">No certifications found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Vendor', 'Certification', 'Issuer', 'Expiry', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {certifications.map(c => (
                  <tr key={c.id} className={`hover:bg-gray-50 ${isExpiringSoon(c.expiry_date) ? 'bg-yellow-50/50' : ''}`}>
                    <td className="px-4 py-3 text-left font-medium text-gray-900">{c.vendor_name}</td>
                    <td className="px-4 py-3 text-left text-gray-700">{c.cert_name}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{c.issuer || '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">
                      {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '—'}
                      {isExpiringSoon(c.expiry_date) && (
                        <span className="ml-1 inline-flex items-center"><AlertCircle size={12} className="text-yellow-600" /></span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${certStatusColors[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          subprocessors.length === 0 ? (
            <div className="py-12 text-gray-500">No subprocessors found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Vendor', 'Subprocessor', 'Service', 'Location', 'DPA in Place'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {subprocessors.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-left font-medium text-gray-900">{s.vendor_name}</td>
                    <td className="px-4 py-3 text-left text-gray-700">{s.subprocessor_name}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{s.service || '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{s.location || '—'}</td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.dpa_in_place ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.dpa_in_place ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Contract Create Modal */}
      {contractModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Contract</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={contractForm.title} onChange={e => setContractForm(f => ({ ...f, title: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor ID</label>
                  <input value={contractForm.vendor_id} onChange={e => setContractForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={contractForm.type} onChange={e => setContractForm(f => ({ ...f, type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['service_agreement', 'dpa', 'nda', 'sla', 'master_agreement', 'other'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DPA Status</label>
                  <select value={contractForm.dpa_status} onChange={e => setContractForm(f => ({ ...f, dpa_status: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['signed', 'pending', 'not_required', 'missing'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Signed Date</label>
                  <input type="date" value={contractForm.signed_date} onChange={e => setContractForm(f => ({ ...f, signed_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input type="date" value={contractForm.expiry_date} onChange={e => setContractForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={contractForm.notes} onChange={e => setContractForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setContractModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreateContract} disabled={submitting || !contractForm.title.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Add Contract'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certification Create Modal */}
      {certModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Certification</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Certification Name *</label>
                <input value={certForm.cert_name} onChange={e => setCertForm(f => ({ ...f, cert_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. ISO 27001" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor ID</label>
                  <input value={certForm.vendor_id} onChange={e => setCertForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issuer</label>
                  <input value={certForm.issuer} onChange={e => setCertForm(f => ({ ...f, issuer: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                  <input type="date" value={certForm.issue_date} onChange={e => setCertForm(f => ({ ...f, issue_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                  <input type="date" value={certForm.expiry_date} onChange={e => setCertForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCertModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreateCert} disabled={submitting || !certForm.cert_name.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Add Certification'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subprocessor Create Modal */}
      {subModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Subprocessor</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subprocessor Name *</label>
                <input value={subForm.subprocessor_name} onChange={e => setSubForm(f => ({ ...f, subprocessor_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor ID</label>
                  <input value={subForm.vendor_id} onChange={e => setSubForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                  <input value={subForm.service} onChange={e => setSubForm(f => ({ ...f, service: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input value={subForm.location} onChange={e => setSubForm(f => ({ ...f, location: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. UK, EU, US" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm pb-2">
                    <input type="checkbox" checked={subForm.dpa_in_place} onChange={e => setSubForm(f => ({ ...f, dpa_in_place: e.target.checked }))} />
                    DPA in Place
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={subForm.notes} onChange={e => setSubForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setSubModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreateSub} disabled={submitting || !subForm.subprocessor_name.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Add Subprocessor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorContracts;
